import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import {
  PrimaryButton,
  PrimaryOutlinedButton,
} from "../../../ui/Button/Button";
import { Placeholder } from "../../../ui/Placeholder";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { Section } from "../../../ui/Section/Section";
import { cartActionSaveQuote } from "../../LeadForm/LeadFormModal";
import { CreatePackageButton } from "../../PackageEditor/CreatePackageButton";
import { BuyNowButton } from "../BuyNowModal/BuyNowButton";
import { useShouldRenderBuyNowButton } from "../BuyNowModal/useShouldRenderBuyNowButton";
import { OrderNowButton } from "../DealerOrderModal/OrderNowButton";
import { useShouldRenderOrderNowButton } from "../DealerOrderModal/useShouldRenderOrderNowButton";
import { useNewQuoteModalState } from "../NewQuoteModal/NewQuoteModal";
import { useOrderDetails } from "../useOrderDetails";

export const CartTotal: FunctionComponent = () => {
  const { t } = useTranslation();
  const [, { show: showNewQuoteModal }] = useNewQuoteModalState();

  const { data, isOrder } = useOrderDetails();

  const shouldRenderOrderButton = useShouldRenderOrderNowButton();
  const shouldRenderBuyNowButton = useShouldRenderBuyNowButton();

  const SaveQuoteButton =
    shouldRenderOrderButton || shouldRenderBuyNowButton
      ? PrimaryOutlinedButton
      : PrimaryButton;

  return (
    <Section className="p-4 md:p-8 space-y-4 md:space-y-8 md:text-lg">
      <h2 className="text-xl md:text-2xl font-medium">
        {t(`cart.header.${isOrder ? "order" : "quote"}`)}
      </h2>
      <div className="flex items-start justify-between">
        <span className="text-ink-2 font-thin">{t("cart.subtotal")}:</span>
        <strong>
          <Placeholder className="w-[4em]" loading={!data}>
            <PriceDisplay>{data?.orderSubtotal}</PriceDisplay>
          </Placeholder>
        </strong>
      </div>
      <div className="flex items-start justify-between">
        <span className="text-ink-2 font-thin">
          {t("cart.tax")}(
          <Placeholder className="w-4" loading={!data}>
            {Number(data?.orderTaxPercent)}
          </Placeholder>
          %):
        </span>
        <strong>
          <Placeholder className="w-[4em]" loading={!data}>
            <PriceDisplay>{data?.orderTaxTotal}</PriceDisplay>
          </Placeholder>
        </strong>
      </div>
      <hr />
      <div className="flex items-start justify-between">
        <span className="text-ink-2 font-thin">{t("cart.total")}:</span>
        <strong>
          <Placeholder className="w-[4em]" loading={!data}>
            <PriceDisplay>{data?.orderGrandTotal}</PriceDisplay>
          </Placeholder>
        </strong>
      </div>
      {!isOrder && (
        <div className="space-y-4">
          <PrimaryOutlinedButton
            aria-label="showNewQuoteModal"
            className="flex h-14 w-full"
            onClick={showNewQuoteModal}
          >
            {t("cart.createNewQuote")}
          </PrimaryOutlinedButton>
          <Link to={`#${cartActionSaveQuote}`}>
            <SaveQuoteButton
              aria-label="nextStep"
              className="flex h-14 w-full"
              element="a"
            >
              {t("cart.nextStep")}
            </SaveQuoteButton>
          </Link>
          <CreatePackageButton id="create-package-button-total" />
          {shouldRenderOrderButton && (
            <OrderNowButton className="flex h-14 w-full" />
          )}
          {shouldRenderBuyNowButton && (
            <BuyNowButton className="flex h-14 w-full" />
          )}
        </div>
      )}
    </Section>
  );
};
